import React, { useContext, useEffect, useState } from 'react'
import { Table, Icon, Ellipsis } from '@cx/ui'
import { useRequest, useDebounceFn } from 'ahooks'
import { AuthContext } from '@/layout/Auth'
import SearchIcon from '@mui/icons-material/Search'
import { MemberInfo } from '@/types'
import { Stack, TextField, Button, Box } from '@mui/material'
import { Card, Text } from './common'
import Modal2 from '@/components/Modal2'
import EditPermissionModal from './EditPermissionModal'
import AddUserModal from './AddUserModal'
import { getMemebers, deleteMember, updateMemberPerms } from '@/api'
import defaultAvatar from '@/asset/img/default_avatar.png'
import { Message } from '@cx/ui'
import { ensureSecurityGate } from '@/utils/securityGate'

function maskName(name: string): string {
  if (!name) return ''
  const length = name.length

  if (length === 1) {
    return '*'
  }
  // 如果只有两个字，则替换最后一个字为 *
  if (length === 2) {
    return `${name[0]}*`
  }

  // 如果超过两个字，则中间的字全部替换为 *
  const firstChar = name[0]
  const lastChar = name[length - 1]
  const maskedMiddle = '*'.repeat(length - 2) // 中间的字符替换成对应长度的 *

  return firstChar + maskedMiddle + lastChar
}

const UserManage = () => {
  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo
  const [visible, setVisible] = useState(false)
  const [addUserVisible, setAddUserVisible] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [userInfo, setUserInfo] = useState<MemberInfo>()
  const {
    data: { data: dataSource, total } = { data: [], total: 0 },
    loading,
    run,
  } = useRequest(
    ({ p = page, size = pageSize, s = search } = {}) =>
      getMemebers({
        id: orgInfo?.id!,
        query: { page: p, size: size, nickname: s },
      }),
    {
      onError: (err) => {
        Message.error(err as unknown as string)
      },
      manual: true,
    },
  )

  const changePage = (page: number, pageSize: number) => {
    setPage(page)
    setPageSize(pageSize)
  }

  const onDeleteUser = (data: MemberInfo) => {
    Modal2.confirm({
      title: '提示',
      okButtonProps: { color: 'error' },
      content: `确认将用户"${
        data?.nickname?.length > 10 ? `${data?.nickname?.slice(0, 10)}...` : data?.nickname
      }"从空间"${orgInfo?.name}"中移除吗？`,
      onOk: async () => {
        deleteMember({ id: orgInfo?.id!, query: { id: data?.id as string } }).then(() => {
          run({ p: 1 })
          setPage(1)
        })
      },
    })
  }

  const onChangePerms = (ids: number[]) => {
    updateMemberPerms({ id: orgInfo?.id!, user_id: userInfo?.id! as number, perm_ids: ids }).then(() => {
      run()
    })
  }

  const debouncedSearch = useDebounceFn(
    (s) => {
      run({ p: 1, s })
      setPage(1)
    },
    { wait: 500 },
  )

  useEffect(() => {
    if (orgInfo?.id) {
      run()
    }
  }, [orgInfo?.id, page, pageSize])

  const columns = [
    {
      dataIndex: 'nickname',
      title: '用户名',
      render(value: string, record: any) {
        return (
          <Stack direction='row' alignItems='center' gap={1}>
            <Box
              component='img'
              sx={{ width: 22, height: 22, borderRadius: '50%' }}
              src={record.head_img_url || defaultAvatar}
            />
            <Ellipsis sx={{ width: 'calc(100% - 32px)', pt: '2px' }}>{value || '-'}</Ellipsis>
          </Stack>
        )
      },
    },

    {
      dataIndex: 'name',
      title: '实名信息',
      width: 180,
      render(value: string, record: any) {
        return (
          maskName(value) || (
            <Stack direction='row' alignItems='center' gap={0.5} sx={{ color: 'rgba(0, 0, 0, 0.20)' }}>
              <Icon type='icon-jinggao'></Icon>
              未实名
            </Stack>
          )
        )
      },
    },
    {
      dataIndex: 'last_logined_at',
      title: '最近登录时间',
      width: 280,
    },
    {
      dataIndex: 'opt',
      title: '操作',
      width: 180,
      render(v: any, record: MemberInfo) {
        return (
          <>
            <Button
              size='small'
              onClick={async () => {
                if (!(await ensureSecurityGate('admin_operation'))) return
                setUserInfo(record)
                setTimeout(() => {
                  setVisible(true)
                })
              }}
            >
              修改权限
            </Button>
            <Button
              size='small'
              color='error'
              onClick={async () => {
                if (!(await ensureSecurityGate('admin_operation'))) return
                onDeleteUser(record)
              }}
            >
              删除
            </Button>
          </>
        )
      },
    },
  ]
  return (
    <Card>
      <EditPermissionModal
        value={userInfo?.perm_ids || []}
        open={visible}
        onCancel={() => {
          setVisible(false)
        }}
        onOk={(ids) => {
          run(1)
          setPage(1)
          setVisible(false)
          onChangePerms(ids)
        }}
      />
      <AddUserModal
        onOk={() => {
          run(1)
          setPage(1)
        }}
        open={addUserVisible}
        onCancel={() => {
          setAddUserVisible(false)
        }}
      />
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 3 }}>
        <Text>用户管理</Text>
        <Stack direction='row' alignItems='center' gap={3}>
          <TextField
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debouncedSearch.run(e.target.value)
            }}
            size='small'
            placeholder='搜索'
            InputProps={{
              endAdornment: <SearchIcon />,
            }}
          />
          <Button
            variant='contained'
            onClick={async () => {
              if (!(await ensureSecurityGate('admin_operation'))) return
              setAddUserVisible(true)
            }}
          >
            添加用户
          </Button>
        </Stack>
      </Stack>

      <Table
        loading={loading}
        columns={columns}
        dataSource={dataSource || []}
        PaginationProps={{
          sx: { m: 3 },
        }}
        sx={{
          mx: -3,
          '.MuiTableCell-root': {
            pl: 0,
            '&:first-of-type': {
              pl: 3,
            },
          },
        }}
        pagination={{
          sx: { mt: 2 },
          total,
          page,
          pageSize,
          onChange: changePage,
        }}
      ></Table>
    </Card>
  )
}

export default UserManage
